import { BaseComponent } from './BaseComponent.js';
import { DOMHelpers } from '../utils/DOMHelpers.js';

/**
 * KnowledgeReview
 * Display and edit product knowledge extracted from docs
 */
export class KnowledgeReview extends BaseComponent {
    constructor(knowledge, options = {}) {
        super();
        this.knowledge = knowledge;
        this.options = {
            onConfirm: options.onConfirm || (() => {}),
            onEdit: options.onEdit || (() => {}),
            onSkip: options.onSkip || (() => {}),
            ...options
        };
        this.editMode = false;
    }

    render() {
        const card = DOMHelpers.createElement('div', {
            id: 'knowledgeReviewCard',
            className: 'knowledge-review-card'
        });

        // Header
        const header = this._renderHeader();
        card.appendChild(header);

        // Product section
        if (this.knowledge.product) {
            const productSection = this._renderProductSection();
            card.appendChild(productSection);
        }

        // Business section
        if (this.knowledge.business) {
            const businessSection = this._renderBusinessSection();
            card.appendChild(businessSection);
        }

        // Phase 2 Discovery V2 Sections
        if (this.knowledge.product) {
            // Buyer Personas (DV2-05)
            if (this.knowledge.product.buyerPersonas) {
                const personasSection = this._renderPersonasSection();
                card.appendChild(personasSection);
            }

            // Positioning (DV2-06)
            if (this.knowledge.product.positioning) {
                const positioningSection = this._renderPositioningSection();
                card.appendChild(positioningSection);
            }

            // Business Model (DV2-04)
            if (this.knowledge.product.businessModel) {
                const businessModelSection = this._renderBusinessModelSection();
                card.appendChild(businessModelSection);
            }

            // Competitive Intelligence (DV2-03)
            if (this.knowledge.product.competitiveIntelligence) {
                const competitiveSection = this._renderCompetitiveSection();
                card.appendChild(competitiveSection);
            }
        }

        // Action buttons
        const actions = this._renderActions();
        card.appendChild(actions);

        this.element = card;
        return card;
    }

    _renderHeader() {
        const header = DOMHelpers.createElement('div', {
            className: 'knowledge-header'
        }, [
            DOMHelpers.createElement('h2', {}, ['📋 Product Knowledge Review']),
            DOMHelpers.createElement('p', {
                className: 'knowledge-subtitle'
            }, ['I extracted this information from your product. Review and confirm or edit as needed.'])
        ]);
        return header;
    }

    _renderProductSection() {
        const section = DOMHelpers.createElement('div', {
            className: 'knowledge-section'
        }, [
            DOMHelpers.createElement('h3', {}, ['🎯 Product Information'])
        ]);

        const p = this.knowledge.product;

        // Name
        if (p.name) {
            section.appendChild(this._renderField('Product Name', p.name));
        }

        // Description
        if (p.description) {
            section.appendChild(this._renderField('Description', p.description));
        }

        // Features
        if (p.features && p.features.length > 0) {
            const hasIncomplete = p.features.some(f =>
                f.includes('incomplete') || f.includes('manual review')
            );

            if (hasIncomplete) {
                section.appendChild(this._renderWarning(
                    '⚠️ Feature extraction was incomplete. You may need to provide more details during discovery.'
                ));
            }

            section.appendChild(this._renderListField('Features', p.features));
        }

        // Target Audience
        if (p.targetAudience) {
            section.appendChild(this._renderField('Target Audience', p.targetAudience));
        }

        // Offer
        if (p.offer && p.offer.value) {
            section.appendChild(this._renderField('Offer', p.offer.value));
        }

        return section;
    }

    _renderBusinessSection() {
        const section = DOMHelpers.createElement('div', {
            className: 'knowledge-section'
        }, [
            DOMHelpers.createElement('h3', {}, ['💼 Business Analysis'])
        ]);

        const b = this.knowledge.business;

        // Positioning
        if (b.positioning) {
            section.appendChild(this._renderField('Positioning', b.positioning));
        }

        // Competitive Advantage
        if (b.competitiveAdvantage && b.competitiveAdvantage.length > 0) {
            section.appendChild(this._renderListField('Competitive Advantages', b.competitiveAdvantage));
        }

        // Primary Buyer
        if (b.targetBuyer && b.targetBuyer.primary) {
            section.appendChild(this._renderField('Primary Buyer', b.targetBuyer.primary));
        }

        // Pain Points
        if (b.targetBuyer && b.targetBuyer.painPoints && b.targetBuyer.painPoints.length > 0) {
            section.appendChild(this._renderListField('Pain Points', b.targetBuyer.painPoints));
        }

        // Unit Economics
        if (b.unitEconomics) {
            const ue = b.unitEconomics;
            const text = [
                ue.ltv ? `LTV: $${ue.ltv}` : null,
                ue.cac ? `CAC: $${ue.cac}` : null,
                ue.ltvCacRatio ? `Ratio: ${ue.ltvCacRatio.toFixed(1)}x` : null
            ].filter(Boolean).join(' | ');

            section.appendChild(this._renderField('Unit Economics', text));
        }

        return section;
    }

    _renderField(label, value) {
        return DOMHelpers.createElement('div', {
            className: 'knowledge-field'
        }, [
            DOMHelpers.createElement('div', {
                className: 'knowledge-label'
            }, [label]),
            DOMHelpers.createElement('div', {
                className: 'knowledge-value'
            }, [value])
        ]);
    }

    _renderListField(label, items) {
        const ul = DOMHelpers.createElement('ul', {});
        items.forEach(item => {
            ul.appendChild(DOMHelpers.createElement('li', {}, [item]));
        });

        return DOMHelpers.createElement('div', {
            className: 'knowledge-field'
        }, [
            DOMHelpers.createElement('div', {
                className: 'knowledge-label'
            }, [label]),
            DOMHelpers.createElement('div', {
                className: 'knowledge-value'
            }, [ul])
        ]);
    }

    _renderWarning(text) {
        return DOMHelpers.createElement('div', {
            className: 'knowledge-warning'
        }, [text]);
    }

    _renderActions() {
        const actions = DOMHelpers.createElement('div', {
            className: 'knowledge-actions'
        });

        // Confirm button
        const confirmBtn = DOMHelpers.createElement('button', {
            className: 'knowledge-btn confirm',
            onClick: () => this.confirm()
        }, ['✓ Looks Good - Continue']);

        // Edit button
        const editBtn = DOMHelpers.createElement('button', {
            className: 'knowledge-btn edit',
            onClick: () => this.enterEditMode()
        }, ['✏️ Edit & Add Info']);

        // Skip button
        const skipBtn = DOMHelpers.createElement('button', {
            className: 'knowledge-btn skip',
            onClick: () => this.skip()
        }, ['→ Start Fresh']);

        actions.appendChild(confirmBtn);
        actions.appendChild(editBtn);
        actions.appendChild(skipBtn);

        return actions;
    }

    confirm() {
        this.options.onConfirm(this.knowledge);
        this.destroy();
    }

    skip() {
        this.options.onSkip();
        this.destroy();
    }

    enterEditMode() {
        if (this.editMode) return;
        this.editMode = true;

        // Replace content with editable form
        const card = this.element;
        card.innerHTML = '';

        const header = DOMHelpers.createElement('div', {
            className: 'knowledge-header'
        }, [
            DOMHelpers.createElement('h2', {}, ['✏️ Edit Product Knowledge']),
            DOMHelpers.createElement('p', {
                className: 'knowledge-subtitle'
            }, ['Edit any fields below or add missing information.'])
        ]);

        const form = this._renderEditForm();
        const saveBtn = DOMHelpers.createElement('button', {
            className: 'btn btn-primary',
            onClick: () => this.saveEdits()
        }, ['💾 Save Changes']);

        const cancelBtn = DOMHelpers.createElement('button', {
            className: 'btn btn-secondary',
            onClick: () => this.cancelEdits()
        }, ['Cancel']);

        const footer = DOMHelpers.createElement('div', {
            className: 'knowledge-actions'
        }, [saveBtn, cancelBtn]);

        card.appendChild(header);
        card.appendChild(form);
        card.appendChild(footer);
    }

    _renderEditForm() {
        const form = DOMHelpers.createElement('form', {
            id: 'editKnowledgeForm',
            className: 'knowledge-edit-form'
        });

        const p = this.knowledge.product || {};
        const b = this.knowledge.business || {};

        // Product fields
        const productSection = DOMHelpers.createElement('div', {
            className: 'knowledge-section'
        }, [
            DOMHelpers.createElement('h3', {}, ['🎯 Product Information'])
        ]);

        productSection.appendChild(this._renderTextarea('Description', 'edit-description', p.description || '', 3));
        productSection.appendChild(this._renderTextarea('Features (one per line)', 'edit-features', p.features ? p.features.join('\n') : '', 5));
        productSection.appendChild(this._renderInput('Target Audience', 'edit-audience', p.targetAudience || ''));
        productSection.appendChild(this._renderInput('Offer', 'edit-offer', p.offer?.value || ''));

        form.appendChild(productSection);

        // Business fields
        const businessSection = DOMHelpers.createElement('div', {
            className: 'knowledge-section'
        }, [
            DOMHelpers.createElement('h3', {}, ['💼 Business Information'])
        ]);

        businessSection.appendChild(this._renderTextarea('Positioning', 'edit-positioning', b.positioning || '', 2));
        businessSection.appendChild(this._renderTextarea('Competitive Advantages (one per line)', 'edit-advantages', b.competitiveAdvantage ? b.competitiveAdvantage.join('\n') : '', 4));
        businessSection.appendChild(this._renderInput('Primary Buyer', 'edit-buyer', b.targetBuyer?.primary || ''));
        businessSection.appendChild(this._renderTextarea('Pain Points (one per line)', 'edit-painpoints', b.targetBuyer?.painPoints ? b.targetBuyer.painPoints.join('\n') : '', 4));

        form.appendChild(businessSection);

        return form;
    }

    _renderInput(label, id, value) {
        const field = DOMHelpers.createElement('div', {
            className: 'knowledge-field'
        });

        const labelEl = DOMHelpers.createElement('label', {
            className: 'knowledge-label',
            for: id
        }, [label]);

        const input = DOMHelpers.createElement('input', {
            type: 'text',
            id,
            className: 'knowledge-input',
            value
        });

        field.appendChild(labelEl);
        field.appendChild(input);
        return field;
    }

    _renderTextarea(label, id, value, rows) {
        const field = DOMHelpers.createElement('div', {
            className: 'knowledge-field'
        });

        const labelEl = DOMHelpers.createElement('label', {
            className: 'knowledge-label',
            for: id
        }, [label]);

        const textarea = DOMHelpers.createElement('textarea', {
            id,
            className: 'knowledge-textarea',
            rows: rows.toString()
        });
        textarea.value = value;

        field.appendChild(labelEl);
        field.appendChild(textarea);
        return field;
    }

    saveEdits() {
        const form = document.getElementById('editKnowledgeForm');
        if (!form) return;

        // Extract form data
        const updatedKnowledge = {
            product: {
                ...this.knowledge.product,
                description: form.querySelector('#edit-description')?.value || '',
                features: form.querySelector('#edit-features')?.value.split('\n').filter(f => f.trim()) || [],
                targetAudience: form.querySelector('#edit-audience')?.value || '',
                offer: {
                    value: form.querySelector('#edit-offer')?.value || ''
                }
            },
            business: {
                ...this.knowledge.business,
                positioning: form.querySelector('#edit-positioning')?.value || '',
                competitiveAdvantage: form.querySelector('#edit-advantages')?.value.split('\n').filter(a => a.trim()) || [],
                targetBuyer: {
                    ...(this.knowledge.business?.targetBuyer || {}),
                    primary: form.querySelector('#edit-buyer')?.value || '',
                    painPoints: form.querySelector('#edit-painpoints')?.value.split('\n').filter(p => p.trim()) || []
                }
            }
        };

        this.knowledge = updatedKnowledge;
        this.options.onEdit(updatedKnowledge);
        this.editMode = false;

        // Re-render with updated data
        const parent = this.element.parentNode;
        this.destroy();
        this.render();
        this.mount(parent);
    }

    cancelEdits() {
        this.editMode = false;
        const parent = this.element.parentNode;
        this.destroy();
        this.render();
        this.mount(parent);
    }

    // DV2-05: Render Buyer Personas Section
    _renderPersonasSection() {
        const personas = this.knowledge.product.buyerPersonas;
        const primary = personas?.primaryPersona;

        if (!primary) return DOMHelpers.createElement('div', {});

        const section = DOMHelpers.createElement('div', {
            className: 'knowledge-section knowledge-section-expandable collapsed'
        }, [
            DOMHelpers.createElement('h3', {
                className: 'knowledge-section-header',
                onClick: (e) => this._toggleSection(e.target.closest('.knowledge-section'))
            }, ['👤 Buyer Personas', DOMHelpers.createElement('span', { className: 'section-toggle' }, ['▼'])])
        ]);

        const content = DOMHelpers.createElement('div', {
            className: 'knowledge-section-content'
        });

        // Primary Persona
        content.appendChild(this._renderField('Primary Persona', primary.name || 'Unnamed'));
        if (primary.tagline) {
            content.appendChild(this._renderField('Tagline', primary.tagline));
        }

        // Demographics
        if (primary.demographics) {
            const demo = primary.demographics;
            const demoText = [
                demo.jobTitle ? `Job Title: ${demo.jobTitle}` : null,
                demo.industry ? `Industry: ${demo.industry}` : null,
                demo.companySize ? `Company Size: ${demo.companySize}` : null
            ].filter(Boolean).join(' | ');
            if (demoText) {
                content.appendChild(this._renderField('Demographics', demoText));
            }
        }

        // Goals
        if (primary.psychographics?.goals?.length > 0) {
            content.appendChild(this._renderListField('Goals', primary.psychographics.goals));
        }

        // Challenges
        if (primary.psychographics?.challenges?.length > 0) {
            content.appendChild(this._renderListField('Challenges', primary.psychographics.challenges));
        }

        // Buying Triggers
        if (primary.buyingBehavior?.triggers?.length > 0) {
            content.appendChild(this._renderListField('Buying Triggers', primary.buyingBehavior.triggers));
        }

        // Objections
        if (primary.buyingBehavior?.objections?.length > 0) {
            content.appendChild(this._renderListField('Objections', primary.buyingBehavior.objections));
        }

        // Confidence Score
        if (personas.confidence) {
            const confidencePercent = Math.round(personas.confidence * 100);
            content.appendChild(this._renderConfidenceBadge(confidencePercent));
        }

        section.appendChild(content);
        return section;
    }

    // DV2-06: Render Positioning Section
    _renderPositioningSection() {
        const positioning = this.knowledge.product.positioning;

        if (!positioning) return DOMHelpers.createElement('div', {});

        const section = DOMHelpers.createElement('div', {
            className: 'knowledge-section knowledge-section-expandable collapsed'
        }, [
            DOMHelpers.createElement('h3', {
                className: 'knowledge-section-header',
                onClick: (e) => this._toggleSection(e.target.closest('.knowledge-section'))
            }, ['🎯 Competitive Positioning', DOMHelpers.createElement('span', { className: 'section-toggle' }, ['▼'])])
        ]);

        const content = DOMHelpers.createElement('div', {
            className: 'knowledge-section-content'
        });

        // Enemy Name
        if (positioning.enemyName) {
            content.appendChild(this._renderField('Enemy', positioning.enemyName));
        }

        // Positioning Statement
        if (positioning.positioningStatement) {
            const stmt = positioning.positioningStatement;
            if (stmt.notThis || stmt.isThis) {
                const text = `NOT: ${stmt.notThis || 'N/A'}\nIS: ${stmt.isThis || 'N/A'}`;
                const field = this._renderField('Positioning Statement', text);
                field.querySelector('.knowledge-value').style.whiteSpace = 'pre-line';
                content.appendChild(field);
            }
        }

        // Truth Bomb
        if (positioning.truthBomb) {
            const field = this._renderField('Truth Bomb', positioning.truthBomb);
            field.querySelector('.knowledge-value').style.fontStyle = 'italic';
            field.querySelector('.knowledge-value').style.fontWeight = '500';
            content.appendChild(field);
        }

        // Confidence Score
        if (positioning.confidence) {
            const confidencePercent = Math.round(positioning.confidence * 100);
            content.appendChild(this._renderConfidenceBadge(confidencePercent));
        }

        section.appendChild(content);
        return section;
    }

    // DV2-04: Render Business Model Section
    _renderBusinessModelSection() {
        const model = this.knowledge.product.businessModel;

        if (!model) return DOMHelpers.createElement('div', {});

        const section = DOMHelpers.createElement('div', {
            className: 'knowledge-section knowledge-section-expandable collapsed'
        }, [
            DOMHelpers.createElement('h3', {
                className: 'knowledge-section-header',
                onClick: (e) => this._toggleSection(e.target.closest('.knowledge-section'))
            }, ['💼 Business Model Analysis', DOMHelpers.createElement('span', { className: 'section-toggle' }, ['▼'])])
        ]);

        const content = DOMHelpers.createElement('div', {
            className: 'knowledge-section-content'
        });

        // Revenue Model
        if (model.revenueModel) {
            content.appendChild(this._renderField('Revenue Model', model.revenueModel.type || 'Unknown'));
        }

        // Pricing Strategy
        if (model.pricingStrategy) {
            content.appendChild(this._renderField('Pricing Strategy', model.pricingStrategy.approach || 'Unknown'));
        }

        // Business Stage
        if (model.businessStage) {
            content.appendChild(this._renderField('Business Stage', model.businessStage.stage || 'Unknown'));
        }

        // Campaign Strategy
        if (model.campaignStrategy?.primaryGoal) {
            content.appendChild(this._renderField('Recommended Goal', model.campaignStrategy.primaryGoal));
        }

        section.appendChild(content);
        return section;
    }

    // DV2-03: Render Competitive Intelligence Section
    _renderCompetitiveSection() {
        const intel = this.knowledge.product.competitiveIntelligence;

        if (!intel?.competitors || intel.competitors.length === 0) {
            return DOMHelpers.createElement('div', {});
        }

        const section = DOMHelpers.createElement('div', {
            className: 'knowledge-section knowledge-section-expandable collapsed'
        }, [
            DOMHelpers.createElement('h3', {
                className: 'knowledge-section-header',
                onClick: (e) => this._toggleSection(e.target.closest('.knowledge-section'))
            }, [`🔍 Competitive Intelligence (${intel.competitors.length} competitors)`, DOMHelpers.createElement('span', { className: 'section-toggle' }, ['▼'])])
        ]);

        const content = DOMHelpers.createElement('div', {
            className: 'knowledge-section-content'
        });

        intel.competitors.forEach((competitor, index) => {
            const profile = intel.analysis?.competitorProfiles?.[index];
            if (!profile) return;

            const competitorCard = DOMHelpers.createElement('div', {
                className: 'competitor-card'
            });

            competitorCard.appendChild(this._renderField('Competitor', profile.name || competitor.url));
            if (profile.valueProp) {
                competitorCard.appendChild(this._renderField('Value Prop', profile.valueProp));
            }
            if (profile.targetAudience) {
                competitorCard.appendChild(this._renderField('Target Audience', profile.targetAudience));
            }

            content.appendChild(competitorCard);
        });

        section.appendChild(content);
        return section;
    }

    // Helper: Render confidence badge
    _renderConfidenceBadge(percent) {
        const badge = DOMHelpers.createElement('div', {
            className: `confidence-badge confidence-${percent >= 80 ? 'high' : percent >= 50 ? 'medium' : 'low'}`
        }, [`Confidence: ${percent}%`]);

        return DOMHelpers.createElement('div', {
            className: 'knowledge-field'
        }, [badge]);
    }

    // Helper: Toggle expandable section
    _toggleSection(section) {
        const isCollapsed = section.classList.contains('collapsed');
        section.classList.toggle('collapsed');
        section.classList.toggle('expanded');

        const toggle = section.querySelector('.section-toggle');
        if (toggle) {
            toggle.textContent = isCollapsed ? '▲' : '▼';
        }
    }
}
